// React
import { Fragment, useEffect, useState } from "react"

// API
import { useMutation, useQuery } from "react-query"
import { AtributApi, SettingApi } from "api"
import { Api } from "./api"

// Components
import { Card, CardBody, Col, InputGroup, Modal, ModalBody, ModalHeader, Input as ReactInput, Row, Table } from "reactstrap"
import { ActionButton, Calendar, Checkbox, CloseButton, FetchingStatus, Input, InputNumber, Select, Td } from "components"

// Form
import * as yup from "yup"
import { Controller, useFormContext, useWatch } from "react-hook-form"
import { ErrorMessage } from "@hookform/error-message"
import { Formik } from "formik"

// Functions
import { dateConvert, toast } from "functions"

// Third-Party Libraries
import moment from "moment"
import { useNavigate } from "react-router-dom"

export function OrderReview({ is_departure, setting }) {
  // Hooks
  const navigate = useNavigate()

  // Form
  const { control, formState, getValues, handleSubmit } = useFormContext()
  const onSubmit = value => {
    return new Promise((resolve) => {
      Api.store({
        ...value,
        walk_in: false
      }).then(res => {
        const data = res.data
  
        localStorage.removeItem('uuid')
        if (value.walk_in || data.redirect) {
          navigate(`/success?unique_id=${data.link}`, { replace: true })
        } else {
          window.location.href = data.link
        }
      }).catch(err => {
        toast("error", "Error", err.response.data.message)
      }).finally(resolve)
    })
  }

  // Variables
  const booking_date = getValues("booking_date")
  const cardStyle = {
    margin: "2rem auto",
    maxWidth: "48.125rem",
  }

  return (
    <div className="container">
      <h4 className="text-center fw-bold">ORDER REVIEW</h4>
      
      <Card className="my-3 rounded-3 shadow-lg" style={cardStyle}>
        <CardBody>
          <section>
            <h5 className="text-center fw-bold mb-4">Booking Details</h5>
            <h6 className="text-center fw-bold">Reservation date: {moment(booking_date).format("dddd, D MMMM YYYY")}</h6>
          </section>

          <TableSection setting={setting} />
        </CardBody>
      </Card>

      <form onSubmit={handleSubmit(onSubmit)} className="my-3">
        <Card className="rounded-3 shadow-lg" style={cardStyle}>
          <CardBody>
            <div className="container-md">
              <h4 className="text-center fw-bold mb-3">How would you like to pay?</h4>

              <PaymentMethod />

              <div className="mb-2">
                <h5 className="fw-bold">Guest Details</h5>

                <Row>
                  <Col md={6}>
                    <Controller
                      control={control}
                      name="first_name"
                      render={({ field, fieldState }) => (
                        <Input
                          {...field}
                          label={<div>First Name <span className="text-danger">*</span></div>}
                          errors={fieldState.error?.message}
                        />
                      )}
                    />
                  </Col>

                  <Col md={6}>
                    <Controller
                      control={control}
                      name="last_name"
                      render={({ field, fieldState }) => (
                        <Input
                          {...field}
                          label={<div>Last Name <span className="text-danger">*</span></div>}
                          errors={fieldState.error?.message}
                        />
                      )}
                    />
                  </Col>

                  <Col md={6}>
                    <Nationality />
                  </Col>

                  <Col md={3}>
                    <Controller
                      control={control}
                      name="phone"
                      render={({ field, fieldState }) => (
                        <Input
                          {...field}
                          label={<div>Phone <span className="text-danger">*</span></div>}
                          errors={fieldState.error?.message}
                        />
                      )}
                    />
                  </Col>

                  <Col md={3}>
                    <Controller
                      control={control}
                      name="zip"
                      render={({ field, fieldState }) => (
                        <Input
                          {...field}
                          label={<div>Zip/Post Code <span className="text-danger">*</span></div>}
                          errors={fieldState.error?.message}
                        />
                      )}
                    />
                  </Col>

                  <Col md={6}>
                    <Gender />
                  </Col>

                  <Col md={6}>
                    <Controller
                      control={control}
                      name="date_of_birth"
                      render={({ field, fieldState }) => (
                        <Calendar
                          label={<div>Date of Birth <span className="text-danger">*</span></div>}
                          errors={fieldState.error?.message}
                          value={new Date(field.value)}
                          onChange={e => field.onChange(dateConvert(e).default.format)}
                        />
                      )}
                    />
                  </Col>

                  <Col md={6}>
                    <Controller
                      control={control}
                      name="email"
                      render={({ field, fieldState }) => (
                        <Input
                          {...field}
                          label={<div>Email <span className="text-danger">*</span></div>}
                          errors={fieldState.error?.message}
                        />
                      )}
                    />
                  </Col>

                  <Col md={6}>
                    <Controller
                      control={control}
                      name="email_confirmation"
                      render={({ field, fieldState }) => (
                        <Input
                          {...field}
                          label={<div>Email Confirmation <span className="text-danger">*</span></div>}
                          errors={fieldState.error?.message}
                        />
                      )}
                    />
                  </Col>
                </Row>

                <Controller
                  control={control}
                  name="terms"
                  render={({ field, fieldState }) => (
                    <Checkbox
                      onChange={() => field.onChange(!field.value)}
                      value={field.value}
                      errors={fieldState.error?.message}
                    >
                      I accept all Finns Recreation Club & Finns Beach Club Booking & venue 
                      <span className="ps-1">
                        <a href="https://bookings.finnsbeachclub.com/superadmin/termcondition/get/fr-general" target="_blank" rel="noreferrer">
                          <ActionButton type="button" color="link" className="border-0 p-0">terms and conditions.</ActionButton>
                        </a>
                      </span>
                      ***
                    </Checkbox>
                  )}
                />

                <Controller
                  control={control}
                  name="policy"
                  render={({ field, fieldState }) => (
                    <Checkbox
                      onChange={() => field.onChange(!field.value)}
                      value={field.value}
                      errors={fieldState.error?.message}
                    >
                      I understand and accept the Finns Recreation Club & Finns Beach Club Booking face & head tattoo <Policy /> ***
                    </Checkbox>
                  )}
                />

                <Controller
                  control={control}
                  name="waiver"
                  render={({ field, fieldState }) => (
                    <Checkbox
                      onChange={() => field.onChange(!field.value)}
                      value={field.value}
                      errors={fieldState.error?.message}
                    >
                      I agree to the 
                      <span className="ps-1">
                        <a href="https://bookings.finnsbeachclub.com/pdf/tc/TC__sw-general.pdf" target="_blank" rel="noreferrer">
                          <ActionButton type="button" color="link" className="border-0 p-0">Splash Waiver.</ActionButton>
                        </a>
                      </span>
                    </Checkbox>
                  )}
                />

                {is_departure && (
                  <Fragment>
                    <Controller
                      control={control}
                      name="no_transportation"
                      render={({ field, fieldState }) => (
                        <Checkbox
                          onChange={() => field.onChange(!field.value)}
                          value={field.value}
                          errors={fieldState.error?.message}
                        >
                          I understand that this ticket does not include transportation
                        </Checkbox>
                      )}
                    />

                    <Controller
                      control={control}
                      name="additional_cost"
                      render={({ field, fieldState }) => (
                        <Checkbox
                          onChange={() => field.onChange(!field.value)}
                          value={field.value}
                          errors={fieldState.error?.message}
                        >
                          I want to request airport transfer (additional charge, subject to availability)
                        </Checkbox>
                      )}
                    />
                  </Fragment>
                )}

                <div className="text-center mt-2">
                  <ActionButton
                    type="submit"
                    loading={formState.isSubmitting}
                    color="primary"
                    className="text-white"
                    size="lg"
                  >
                    Pay Now
                  </ActionButton>
                </div>
              </div>
            </div>
          </CardBody>
        </Card>
      </form>
    </div>
  )
}

function TableSection({ setting }) {
  // Hooks
  const { control } = useFormContext()

  // Variables
  const _code = useWatch({
    control,
    name: "_code"
  })
  const qty_insurance = useWatch({
    control,
    name: "qty_insurance"
  })
  const ticket = useWatch({
    control,
    name: "ticket"
  })

  // API
  const getDataCurrency = useQuery(['dayPasses-payment-currency'], AtributApi.currency_rate)
  const fetchInsurance = useQuery(['setting', 'personal_insurance'], SettingApi.personal_insurance)

  // Variables
  const totalPrice = ticket.reduce(
    (previousValue, { pax, price }) => previousValue + (pax * price), 0
  )
  const totalDiscount = () => {
    let discount = 0

    if (_code.length === 0) {
      discount = 0
    } else {
      discount = ticket.reduce((acc, item) => {
        if (_code[0].promo.sub_type === "GLOBAL" || _code[0].promo.service_available.find(code => code.service_id === item.id)) {
          return acc += _code[0].discount_type === "PERCENTAGE" ? (item.price * (_code[0].amount / 100) * item.pax) : _code[0].amount
        }
    
        return acc
      }, 0)
    }

    return totalPrice - discount
  }
  const tax = totalDiscount() * setting.tax_services
  const insurance_price = qty_insurance * (fetchInsurance.data ?? 0)
  const grandTotal = totalDiscount() + tax + insurance_price
  const ticketPax = ticket.reduce((acc, value) => acc + value.pax, 0)

  return (
    <Table responsive>
      <thead>
        <tr className="bg-light">
          <th>Bed Details</th>
          <th style={{ width: '300px' }}></th>
        </tr>
      </thead>
      <tbody>
        {ticket.map((val, index) => (
          <tr key={index}>
            <Td>
              <div className="d-flex justify-center-start align-items-center">
                <img height={60} className="me-2" alt={val.name} src={`${process.env.REACT_APP_BASE_URL}${val.image}`} />
                <div className="fw-bold">{val.pax}x {val.name} {val.service_name && <span>({val.service_name})</span>}</div>
              </div>
            </Td>
            <Td>
              <TdEnd>IDR {(val.price * parseInt(val.pax)).toLocaleString('id-ID')}</TdEnd>
            </Td>
          </tr>
        ))}
        <tr>
          <Td>
            <div className="fw-bold">Sub Total</div>
          </Td>
          <Td>
            <TdEnd>IDR {totalPrice.toLocaleString('id-ID')}</TdEnd>
          </Td>
        </tr>

        <Code />

        <tr>
          <Td>
            <div className="d-flex align-items-center">
              <div className="fw-bold me-1">Personal Insurance</div>

              <Controller
                control={control}
                name="qty_insurance"
                render={({ field }) => (
                  <InputNumber
                    {...field}
                    noLabel
                    noMargin
                    min={0}
                    max={ticketPax}
                    className="cart-input"
                  />
                )}
              />
            </div>
          </Td>
          <Td>
            <TdEnd>IDR {insurance_price.toLocaleString('id-ID')}</TdEnd>
          </Td>
        </tr>

        {setting.tax_services > 0 && (
          <tr>
            <Td>
              <div className="fw-bold">Government Tax & Service Charge</div>
            </Td>
            <Td>
              <TdEnd>IDR {tax.toLocaleString('id-ID')}</TdEnd>
            </Td>
          </tr>
        )}

        <tr className="bg-light">
          <Td>
            <div className="fw-bold">Grand Total</div>
          </Td>
          <Td>
            <TdEnd>IDR {grandTotal.toLocaleString('id-ID')}</TdEnd>
          </Td>
        </tr>

        {getDataCurrency.isLoading || getDataCurrency.isError ? (
          <tr>
            <Td colSpan={2}>
              <FetchingStatus loading={getDataCurrency.isLoading} />
            </Td>
          </tr>
        ) : (
          <tr>
            <ConversionSection grandTotal={grandTotal} getDataCurrency={getDataCurrency} />
          </tr>
        )}
      </tbody>
    </Table>
  )
}

function ConversionSection({ getDataCurrency, grandTotal }) {
  // States
  const [dataConversion, setDataConversion] = useState({
    value: getDataCurrency.data[0].value,
    data: getDataCurrency.data[0]
  })

  const handleConversion = (e) => {
    const value = e.target.value
    const data = getDataCurrency.data.find(val => String(val.value) === String(value))

    setDataConversion({ value, data })
  }

  const convertedPrice = data => {
    return grandTotal / 1000 * parseFloat(data.rate)
  }

  const dataConvertedDollar = getDataCurrency.data.find(val => val.label === "USD")

  return (
    <Fragment>
      <Td>
        <div className="d-flex justify-content-start align-items-center">
          <div className="fw-bold me-2">Currency Converter</div>
          <Input noMargin noLabel type="select" value={dataConversion.value} style={{ width: '100px' }} onChange={handleConversion}>
            {getDataCurrency.data.map((val, index) => <option key={index} value={val.value} className="me-2">{val.label}</option>)}
          </Input>
        </div>
        <small>Currency conversion rates are estimates only and subject to change based on your financial institution and payment gateway conversion rates. This is an example only, you will be charged based on the financial gateway provider currency.</small>
      </Td>
      <Td className="text-end">
        <div className="fw-bold">{dataConversion.data.label} {convertedPrice(dataConversion.data).toLocaleString('id-ID')}</div>
        <small>(estimate {dataConvertedDollar.label} {convertedPrice(dataConvertedDollar).toLocaleString('id-ID')})</small>
      </Td>
    </Fragment>
  )
}

function PaymentMethod() {
  // API
  const getDataPayments = useQuery(['dayPasses-payment-paymentMethod'], AtributApi.payment_methods)

  // Form
  const { control, formState, setValue } = useFormContext()

  useEffect(() => {
    if (!getDataPayments.isLoading && !getDataPayments.isError) {
      setValue("payment_method", getDataPayments.data?.find((_, index) => index === 0)?.name ?? "")
    }

    // eslint-disable-next-line
  }, [getDataPayments])

  if (getDataPayments.isLoading || getDataPayments.isError) {
    return <FetchingStatus loading={getDataPayments.isLoading} />
  } else {
    return (
      <Row className="justify-content-center align-items-stretch text-center mb-3">
        <Controller
          control={control}
          name="payment_method"
          render={({ field, fieldState }) => (
            <Fragment>
              {getDataPayments.data.map((val, index) => {
                // Variables
                const isSelected = Boolean(field.value === val.name)
                const image = val.image ?? "[]"
        
                return (
                  <Col lg={4} md={6} key={index} className="d-flex align-items-stretch my-1">
                    <Card
                      className={`border-${Boolean(fieldState.error) ? 'danger' : isSelected ? 'primary' : 'secondary'} shadow-none w-100`}
                      style={{ backgroundColor: isSelected && 'rgba(104, 204, 203, 0.1)', cursor: 'pointer' }}
                      onClick={() => field.onChange(val.name)}
                    >
                      <CardBody className="d-flex flex-column text-center">
                        <div className="d-flex flex-wrap justify-content-center mb-2">
                          {JSON.parse(image).map((val, index) => (
                            <img key={index} alt="Payment" style={{ border: '2px solid rgb(178, 190, 181)' }} src={`${process.env.REACT_APP_BASE_URL}${val}`} height="35" className="m-1" />
                          ))}
                        </div>
        
                        <h6 className="mt-auto fw-bold">{val.display_name}</h6>
                      </CardBody>
                    </Card>
                  </Col>
                )
              })}

              <ErrorMessage
                errors={formState.errors}
                name="payment_method"
                render={({ message }) => <small className="text-danger">{message}</small>}
              />
            </Fragment>
          )}
        />
      </Row>
    )
  }
}

function Policy() {
  // Hooks
  const [isOpen, setOpen] = useState(false)

  const toggle = () => {
    setOpen(prev => !prev)
  }

  return (
    <Fragment>
      <span>
        <ActionButton type="button" color="link" className="border-0 p-0" onClick={toggle}>policy.</ActionButton>
      </span>

      {isOpen && (
        <Modal isOpen toggle={toggle} centered size="lg">
          <ModalHeader toggle={toggle}>Policy</ModalHeader>

          <ModalBody className="text-center">
            <img src={require('assets/images/policy.jpeg')} alt="Policy" className="img-fluid" />
          </ModalBody>
        </Modal>
      )}
    </Fragment>
  )
}

function Gender() {
  // API
  const fetchGenders = useQuery(['dayPasses-genders'], AtributApi.genders)

  // Form
  const { control } = useFormContext()

  return (
    <Controller
      control={control}
      name="gender"
      render={({ field, fieldState }) => (
        <Select
          label={<div>Gender <span className="text-danger">*</span></div>}
          placeholder="Select Gender..."
          loading={fetchGenders.isLoading}
          defaultValue={fetchGenders?.data?.find(val => val.value === field.value)}
          options={fetchGenders?.data}
          errors={fieldState.error?.message}
          onChange={e => field.onChange(e.value)}
        />
      )}
    />
  )
}

function Nationality() {
  // API
  const fetchCountries = useQuery(['dayPasses-countries'], AtributApi.countries)

  // Form
  const { control } = useFormContext()

  return (
    <Controller
      control={control}
      name="nationality"
      render={({ field, fieldState }) => (
        <Select
          label={<div>Nationality <span className="text-danger">*</span></div>}
          placeholder="Select Nationality..."
          loading={fetchCountries.isLoading}
          defaultValue={fetchCountries?.data?.find(val => val.value === field.value)}
          options={fetchCountries?.data}
          onChange={e => {
            field.onChange(e.value)
            // setValues({
            //   ...values,
            //   nationality: e.value,
            //   _nationality: e
            // })
          }}
          errors={fieldState.error?.message}
        />
      )}
    />
  )
}

function Code() {
  // API
  const deletePromo = useMutation(value => Api.deletePromo(value))

  // Form
  const { control, getValues, setValue } = useFormContext()
  const _code = useWatch({
    control,
    name: '_code'
  })
  const ticket = useWatch({
    control,
    name: 'ticket'
  })

  // Variables
  const booking_date = getValues("booking_date")

  const InputPromo = ({ onSuccess }) => (
    <Formik
      initialValues={{
        booking_date,
        code: "",
        unique_id: localStorage.getItem('uuid')
      }}
      validationSchema={yup.object().shape({
        code: yup.string().required()
      })}
      onSubmit={(values, { setSubmitting, resetForm }) => {
        Api.promo(values).then(res => {
          resetForm()
          onSuccess(res.promo)
          toast("success", "Success", res.message)
        }).catch(err => {
          toast("error", "Failed", err.response.data.message)
        }).finally(() => {
          setSubmitting(false)
        })
      }}
    >
      {({values, errors, isSubmitting, handleSubmit, handleChange}) => {
        return (
          <Td colSpan={2}>
            <section>
              <div  className="d-flex flex-md-row flex-column justify-content-md-between justify-content-stretch align-items-md-center align-items-start">
                <div className="fw-bold">Promo Code</div>
  
                <section>
                  <InputGroup>
                    <ReactInput name="code" value={values.code} onChange={handleChange} />
  
                    <ActionButton
                      type="button"
                      className="px-1 text-white"
                      color="primary"
                      loading={isSubmitting}
                      onClick={handleSubmit}
                    >
                      Submit
                    </ActionButton>
                  </InputGroup>
  
                  {errors.code && <small className="text-danger">{errors.code}</small>}
                </section>
              </div>
            </section>
          </Td>
        )
      }}
    </Formik>
  )

  return (
    <Fragment>
      {_code.map((val, index) => {
          // const isBalance = Boolean(val.discount_type === "BALANCE")
          const isFixed = Boolean(val.discount_type === "FIXED")
          const isPercentage = Boolean(val.discount_type === "PERCENTAGE")
          const totalDiscount = ticket.reduce((acc, item) => {
            if (val.promo.sub_type === "GLOBAL" || val.promo.service_available.find(val => val.service_id === item.id)) {
              return acc += isPercentage ? (item.price * (val.amount / 100) * item.pax) : val.amount
            }

            return acc
          }, 0)

          return (
            <tr key={index}>
              <Td>
                <div className="fw-bold">Discount</div>
                <small>{isFixed && "Rp. "}{val.amount.toLocaleString('id-ID')}{isPercentage && "%"} off {val.promo_name ? `- ${val.promo_name}` : ""}</small>
              </Td>
              <Td>
                <section className="d-flex justify-content-end">
                  <TdEnd>IDR {totalDiscount.toLocaleString('id-ID')}</TdEnd>
                  <CloseButton
                    onClick={() => {
                      const data = [..._code]
                      data.splice(index, 1)

                      setValue('_code', data)
                      deletePromo.mutate(val.id)
                    }}
                  />
                </section>
              </Td>
            </tr>
          )
        })}

        {_code.length === 0 && (
          <tr>
            <InputPromo
              onSuccess={data => {
                const value = [..._code]
                value.push(data)

                setValue('_code', value)
              }}
            />
          </tr>
        )}
    </Fragment>
  )
}

function TdEnd({ children }) {
  return (
    <div className="fw-bold text-end">{children}</div>
  )
}